import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../lib/api';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, User, LogOut, LayoutDashboard, Shield, CreditCard, Sun, Moon, Bell, MessageSquare, Settings } from 'lucide-react';
import { ThemeLogo } from './ThemeLogo';
import { AppSwitcher } from './AppSwitcher';

export const Navbar = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const { user, isAuthenticated, isAdmin, logout, token } = useAuth();
    const { isDark, toggleTheme, themeToggleEnabled } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();

    const fetchUnreadCount = useCallback(async () => {
        if (!isAdmin || !token) return;
        try {
            const data = await api.getUnreadMessageCount();
            setUnreadCount(data.count);
        } catch {
            // silently fail
        }
    }, [isAdmin, token]);

    useEffect(() => {
        if (!isAdmin || !token) return;
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 60000); // 60 saniye
        return () => clearInterval(interval);
    }, [isAdmin, token]); // eslint-disable-line react-hooks/exhaustive-deps

    const navLinks = [
        { href: '/', label: 'Ana Sayfa' },
        { href: '/uygulamalar', label: 'Uygulamalar' },
        { href: '/fiyatlandirma', label: 'Fiyatlandırma' },
        { href: '/nasil-calisir', label: 'Nasıl Çalışır?' },
        { href: '/hakkimizda', label: 'Hakkımızda' },
        { href: '/iletisim', label: 'İletişim' },
        { href: '/sss', label: 'SSS' },
    ];

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const isActive = (path) => location.pathname === path;

    return (
        <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="navbar">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                        <ThemeLogo className="h-10" />
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                to={link.href}
                                className={`nav-link text-sm font-medium transition-colors ${
                                    isActive(link.href)
                                        ? 'text-foreground'
                                        : 'text-muted-foreground hover:text-foreground'
                                }`}
                                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>

                    {/* Right side: Theme + Notifications + Auth */}
                    <div className="hidden md:flex items-center gap-2">
                        {/* Theme Toggle */}
                        {themeToggleEnabled && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            className="theme-toggle text-muted-foreground hover:text-foreground hover:bg-accent/50"
                            data-testid="theme-toggle-btn"
                        >
                            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        </Button>
                        )}

                        {/* App Switcher (visible to authenticated users) */}
                        {isAuthenticated && <AppSwitcher />}

                        {/* Notification Bell (Admin only) */}
                        {isAdmin && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="relative text-muted-foreground hover:text-foreground hover:bg-accent/50"
                                        data-testid="notification-bell-btn"
                                    >
                                        <Bell className="w-5 h-5" />
                                        {unreadCount > 0 && (
                                            <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center unread-dot" data-testid="unread-badge">
                                                {unreadCount > 9 ? '9+' : unreadCount}
                                            </span>
                                        )}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-72">
                                    <div className="px-3 py-2 border-b border-border">
                                        <p className="text-foreground font-medium text-sm">Bildirimler</p>
                                        {unreadCount > 0 && (
                                            <p className="text-xs text-primary">{unreadCount} okunmamış mesaj</p>
                                        )}
                                    </div>
                                    {unreadCount > 0 ? (
                                        <DropdownMenuItem asChild>
                                            <Link 
                                                to="/admin" 
                                                className="flex items-center gap-3 px-3 py-3 cursor-pointer"
                                                data-testid="notification-messages-link"
                                            >
                                                <div className="w-8 h-8 rounded-full bg-blue-600/20 flex items-center justify-center flex-shrink-0">
                                                    <MessageSquare className="w-4 h-4 text-primary" />
                                                </div>
                                                <div>
                                                    <p className="text-foreground text-sm font-medium">{unreadCount} yeni mesaj</p>
                                                    <p className="text-muted-foreground text-xs">Mesajları görüntülemek için tıklayın</p>
                                                </div>
                                            </Link>
                                        </DropdownMenuItem>
                                    ) : (
                                        <div className="px-3 py-6 text-center">
                                            <Bell className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                                            <p className="text-muted-foreground text-sm">Yeni bildirim yok</p>
                                        </div>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        {/* Auth */}
                        {isAuthenticated ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button 
                                        variant="ghost" 
                                        className="flex items-center gap-2 text-muted-foreground hover:text-foreground hover:bg-accent/50"
                                        data-testid="user-menu-trigger"
                                    >
                                        <User className="w-4 h-4" />
                                        <span>{user?.name}</span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                    <DropdownMenuItem asChild>
                                        <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer" data-testid="dashboard-link">
                                            <LayoutDashboard className="w-4 h-4" />
                                            <span>Dashboard</span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/aboneliklerim" className="flex items-center gap-2 cursor-pointer" data-testid="subscriptions-link">
                                            <CreditCard className="w-4 h-4" />
                                            <span>Aboneliklerim</span>
                                        </Link>
                                    </DropdownMenuItem>
                                    {isAdmin && (
                                        <DropdownMenuItem asChild>
                                            <Link to="/admin" className="flex items-center gap-2 cursor-pointer" data-testid="admin-link">
                                                <Shield className="w-4 h-4" />
                                                <span>Admin Panel</span>
                                            </Link>
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem asChild>
                                        <Link to="/ayarlar" className="flex items-center gap-2 cursor-pointer" data-testid="settings-link">
                                            <Settings className="w-4 h-4" />
                                            <span>Ayarlar</span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                        onClick={handleLogout} 
                                        className="flex items-center gap-2 cursor-pointer text-red-500 focus:text-red-500"
                                        data-testid="logout-button"
                                    >
                                        <LogOut className="w-4 h-4" />
                                        <span>Çıkış Yap</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <>
                                <Link to="/giris">
                                    <Button variant="ghost" className="text-muted-foreground hover:text-foreground hover:bg-accent/50" data-testid="login-button">
                                        Giriş Yap
                                    </Button>
                                </Link>
                                <Link to="/kayit">
                                    <Button className="btn-primary" data-testid="register-button">
                                        Kayıt Ol
                                    </Button>
                                </Link>
                            </>
                        )}
                    </div>

                    {/* Mobile: Theme + Menu */}
                    <div className="flex md:hidden items-center gap-2">
                        {themeToggleEnabled && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            className="theme-toggle text-muted-foreground hover:text-foreground"
                            data-testid="mobile-theme-toggle"
                        >
                            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        </Button>
                        )}

                        {isAdmin && unreadCount > 0 && (
                            <Link to="/admin" className="relative" data-testid="mobile-notification-link">
                                <Bell className="w-5 h-5 text-muted-foreground" />
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[9px] font-bold text-white flex items-center justify-center unread-dot">
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            </Link>
                        )}

                        <button
                            className="text-muted-foreground hover:text-foreground"
                            onClick={() => setMobileOpen(!mobileOpen)}
                            data-testid="mobile-menu-button"
                        >
                            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            {mobileOpen && (
                <div className="md:hidden glass border-t border-border" data-testid="mobile-menu">
                    <div className="px-4 py-4 space-y-3">
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                to={link.href}
                                className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    isActive(link.href)
                                        ? 'bg-primary/20 text-foreground'
                                        : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                                }`}
                                onClick={() => setMobileOpen(false)}
                            >
                                {link.label}
                            </Link>
                        ))}
                        <div className="pt-3 border-t border-border space-y-2">
                            {isAuthenticated ? (
                                <>
                                    <Link
                                        to="/dashboard"
                                        className="block px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-accent/50 hover:text-foreground"
                                        onClick={() => setMobileOpen(false)}
                                    >
                                        Dashboard
                                    </Link>
                                    {isAdmin && (
                                        <Link
                                            to="/admin"
                                            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-accent/50 hover:text-foreground"
                                            onClick={() => setMobileOpen(false)}
                                        >
                                            Admin Panel
                                            {unreadCount > 0 && (
                                                <span className="w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center">
                                                    {unreadCount}
                                                </span>
                                            )}
                                        </Link>
                                    )}
                                    <Link
                                        to="/ayarlar"
                                        className="block px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-accent/50 hover:text-foreground"
                                        onClick={() => setMobileOpen(false)}
                                    >
                                        Ayarlar
                                    </Link>
                                    <button
                                        onClick={() => { handleLogout(); setMobileOpen(false); }}
                                        className="block w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-red-500 hover:bg-red-500/10"
                                    >
                                        Çıkış Yap
                                    </button>
                                </>
                            ) : (
                                <>
                                    <Link
                                        to="/giris"
                                        className="block px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-accent/50 hover:text-foreground"
                                        onClick={() => setMobileOpen(false)}
                                    >
                                        Giriş Yap
                                    </Link>
                                    <Link
                                        to="/kayit"
                                        className="block px-3 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground text-center"
                                        onClick={() => setMobileOpen(false)}
                                    >
                                        Kayıt Ol
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};
